import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { styled } from "styled-components";
import { motion } from "framer-motion";
import { FaArrowRight } from "react-icons/fa6";
import { fadeInTopVariant, fadeInRightVariant } from "../utils/variants";
import BlueBrush from "../assets/brush-paint-stroke.webp";
import RedBrush from "../assets/red-paint-brush-stroke.webp";

const Home = () => {
  const { t } = useTranslation();

  return (
    <MainContainer>
      {/* Accent décoratif flou en arrière-plan */}
      <GlowAccent />

      <SectionContainer>
        {/* Colonne image — masquée sur mobile */}
        <ArticleImg
          as={motion.article}
          variants={fadeInRightVariant}
          initial="hidden"
          whileInView="visible"
          transition={{ type: "tween", duration: 1 }}
        >
          <BlueBrushImg src={BlueBrush} alt="peinture bleue décorative" />
          <RedBrushImg src={RedBrush} alt="peinture rouge décorative" />
        </ArticleImg>

        {/* Colonne texte */}
        <ArticleText>
          <TitleHome
            as={motion.h1}
            variants={fadeInTopVariant}
            initial="hidden"
            whileInView="visible"
            transition={{ type: "tween", duration: 2, delay: 1 }}
          >
            JOHANE OMISCAR
          </TitleHome>

          <TextContainer
            as={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <JobWrapper>
              <Job>{t("home.job_title")}</Job>
              <JobDivider />
              <Job>{t("home.subJobTitle")}</Job>
            </JobWrapper>

            <DescribeBlock>
              <Describe>{t("home.text")}</Describe>
              <Describe>{t("home.subText1")}</Describe>
              <Describe>{t("home.subText2")}</Describe>
              <Describe>{t("home.text2")}</Describe>
            </DescribeBlock>

            <ButtonContainer
              whileHover={{
                scale: 1.05,
                textShadow: "0px 0px 8px rgb(255, 255, 255)",
                boxShadow: "0px 0px 8px rgba(255, 255, 255, 0.3)",
              }}
              whileTap={{ scale: 0.97 }}
            >
              <Link to="/works" className="btn_works">
                {t("home.button")}
                <motion.span
                  animate={{ x: [0, 6, 0], opacity: [1, 0.5, 1] }}
                  transition={{
                    duration: 1.8,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <FaArrowRight />
                </motion.span>
              </Link>
            </ButtonContainer>
          </TextContainer>
        </ArticleText>
      </SectionContainer>
    </MainContainer>
  );
};

export default Home;

/* ─── Layout principal ────────────────────────────────────────────────────── */

const MainContainer = styled.main`
  position: relative;
  z-index: 0;
  max-width: 80%;
  margin: 0 auto;
  padding: 8rem 0 5rem 0;
  overflow: hidden; /* contient le glow */
  min-height: 100vh;

  @media (min-width: 2400px) {
    max-width: 75%;
    //min-height: 100vh;
    display: flex;
    align-items: center;
  }

  @media (max-width: 1024px) {
    max-width: 88%;
  }

  @media (max-width: 768px) {
    max-width: 90%;
    padding: 7rem 0 4rem 0;
  }

  @media (max-width: 480px) {
    max-width: 88%;
    padding: 6rem 0 3rem 0;
    text-align: center;
  }
`;

/* Cercle flou décoratif — reprend l'ambiance rouge/bordeaux du design */
const GlowAccent = styled.div`
  position: absolute;
  top: 10%;
  left: -8%;
  width: 420px;
  height: 420px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(180, 1, 18, 0.12) 0%,
    transparent 70%
  );
  pointer-events: none;
  filter: blur(40px);
  z-index: 0;

  @media (max-width: 768px) {
    width: 260px;
    height: 260px;
    top: 5%;
    left: -15%;
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

const SectionContainer = styled.section`
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 500px;
  gap: 2rem;
  align-items: center;
  margin-top: 3rem;
  min-height: calc(100vh - 13rem);

  @media (min-width: 2400px) {
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    margin-top: 4rem;
  }

  @media (min-width: 1440px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 380px;
    gap: 2rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr 260px;
    gap: 1.5rem;
  }

  @media (max-width: 576px) {
    /* On empile : texte d'abord, image cachée */
    display: block;
  }
`;

/* ─── Colonne image ───────────────────────────────────────────────────────── */

const ArticleImg = styled.article`
  position: relative;
  width: 20rem;
  height: 30rem;
  flex-shrink: 0;

  @media (min-width: 2400px) {
    width: 30rem;
    height: 42rem;
  }

  @media (min-width: 1440px) {
    width: 26rem;
    height: 36rem;
  }

  @media (max-width: 1024px) {
    width: 18rem;
    height: 27rem;
  }

  @media (max-width: 768px) {
    width: 13rem;
    height: 20rem;
  }

  /* Caché sous 576px — le texte prend toute la largeur */
  @media (max-width: 576px) {
    display: none;
  }
`;

const BlueBrushImg = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
`;

const RedBrushImg = styled.img`
  position: absolute;
  top: 65px;
  left: 8rem;
  width: 15rem;
  height: auto;

  @media (min-width: 2400px) {
    width: 28rem;
    left: 12rem;
    top: 80px;
  }

  @media (min-width: 1440px) {
    width: 22rem;
    left: 10rem;
  }

  @media (max-width: 1024px) {
    width: 14rem;
    left: 7rem;
    top: 55px;
  }

  @media (max-width: 768px) {
    width: 9rem;
    left: 5rem;
    top: 40px;
  }
`;

/* ─── Colonne texte ───────────────────────────────────────────────────────── */

const ArticleText = styled.article`
  /* Rien de fixe — s'étire dans la colonne */
`;

const TitleHome = styled.h1`
  font-size: 3.2rem;
  letter-spacing: 0.04em;
  line-height: 1.1;
  margin-bottom: 1.2rem;

  /* Soulignement discret façon pinceau */
  position: relative;
  display: inline-block;

  &::after {
    content: "";
    position: absolute;
    bottom: -6px;
    left: 0;
    width: 60%;
    height: 2px;
    background: linear-gradient(90deg, rgba(180, 1, 18, 0.7), transparent);
    border-radius: 2px;
  }

  @media (min-width: 2400px) {
    font-size: 6rem;
  }

  @media (min-width: 1440px) {
    font-size: 3.8rem;
  }

  @media (max-width: 1024px) {
    font-size: 2.8rem;
  }

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.9rem;

    &::after {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const TextContainer = styled.div`
  padding-bottom: 1rem;
`;

/* Titres de poste groupés avec séparateur */
const JobWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  flex-wrap: wrap;
  margin-bottom: 1.2rem;

  @media (max-width: 480px) {
    justify-content: center;
  }
`;

const JobDivider = styled.span`
  display: inline-block;
  width: 1px;
  height: 1.1em;
  background: rgba(180, 1, 18, 0.6);
  flex-shrink: 0;

  @media (max-width: 480px) {
    display: none;
  }
`;

const Job = styled.p`
  font-style: italic;
  color: #d8d8d8;
  font-weight: bold;
  font-size: 1.2rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0;

  @media (min-width: 2400px) {
    font-size: 2rem;
  }

  @media (min-width: 1440px) {
    font-size: 1.5rem;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const DescribeBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin-bottom: 0.5rem;
  padding-left: 0.6rem;
  border-left: 2px solid rgba(255, 255, 255, 0.08);

  @media (max-width: 480px) {
    border-left: none;
    padding-left: 0;
    align-items: center;
  }
`;

const Describe = styled.p`
  color: #a8a29e;
  font-size: 1rem;
  line-height: 1.6;
  margin: 0;

  @media (min-width: 2400px) {
    font-size: 1.8rem;
  }

  @media (min-width: 1440px) {
    font-size: 1.2rem;
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

export const ButtonContainer = styled(motion.button)`
  width: fit-content;
  padding: 0.75rem 1.4rem;
  background: transparent;
  border: 1px solid rgba(250, 250, 250, 0.55);
  border-radius: 2rem;
  margin-top: 1.8rem;
  cursor: pointer;
  transition: border-color 0.3s ease;

  a {
    font-family: "Gowun Batang", serif;
    font-size: 1rem;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    color: #fafafa;
    text-decoration: none;
  }

  span {
    display: flex;
    align-items: center;
  }

  &:hover {
    border-color: rgba(180, 1, 18, 0.7);
  }

  @media (min-width: 2400px) {
    padding: 1rem 2rem;

    a {
      font-size: 1.6rem;
    }
  }

  @media (min-width: 1440px) {
    a {
      font-size: 1.15rem;
    }
  }

  @media (max-width: 480px) {
    margin: 1.8rem auto 0 auto;

    a {
      font-size: 0.9rem;
    }
  }
`;
